// Dispatch readiness — the ONE place the screens decide what Mark Dispatched
// needs (client, 2026-10-07). It mirrors src/shared/dispatch/local-transport.js
// exactly; both Dispatch pages use it, so the rule can no longer drift between
// the list page, the order page and the server.
//
//  - Local transport details (name, mobile, vehicle): only when the order uses
//    local transport — Sales said "yes" at hand-off, or the transport mode IS
//    local transport. "Not asked" on a courier order means not needed.
//  - POD: never needed to DISPATCH (it only exists after delivery). It is
//    needed to mark the order DELIVERED; credit orders are exempt.

type Doc = { url?: string; kind?: string; fileUrl?: string };
type Handoff = {
  localTransportUsed?: boolean | null;
  transportMode?: string;
  localTransport?: Record<string, unknown> | null;
  podCopies?: Doc[];
  paymentArrangement?: string;
  paymentDetails?: { paymentTerms?: string };
} | null | undefined;

const has = (v: unknown) => String(v ?? '').trim() !== '';

export function isCreditHandoff(handoff: Handoff): boolean {
  if (!handoff) return false;
  const terms = String(handoff.paymentDetails?.paymentTerms || '').toLowerCase();
  if (terms.startsWith('credit')) return true;
  const arrangement = String(handoff.paymentArrangement || '').trim().toLowerCase();
  return arrangement === 'credit' || arrangement.startsWith('credit');
}

export function localTransportApplies(handoff: Handoff): boolean {
  const used = handoff?.localTransportUsed ?? null;
  if (used === true) return true;
  if (used === false) return false;
  return String(handoff?.transportMode || '').trim().toLowerCase() === 'local_transport';
}

export function hasLocalTransportDetails(handoff: Handoff): boolean {
  const lt = (handoff?.localTransport || {}) as Record<string, unknown>;
  return has(lt.name);
}

/** Mark Dispatched must first collect the local transport details. */
export function needsLocalTransportDetails(handoff: Handoff): boolean {
  return localTransportApplies(handoff) && !hasLocalTransportDetails(handoff);
}

export function hasPod(handoff: Handoff, docs: Doc[] = []): boolean {
  return (handoff?.podCopies || []).some((d) => has(d?.url))
    || docs.some((d) => d?.kind === 'pod' && has(d.url ?? d.fileUrl));
}

/** Mark Delivered must first have the POD (non-credit orders). */
export function needsPodForDelivery(handoff: Handoff, docs: Doc[] = []): boolean {
  return !isCreditHandoff(handoff) && !hasPod(handoff, docs);
}
